<script setup lang="ts">
import { Options } from "highcharts";
import { computed } from "vue";

import { EDashboardType } from "@/app/data/dashboard/dashboard.types";
import { TTemporalDataResponseSerie } from "@/components/ui/chart/types/temporal-chart-series.type";
import { EYearConsumption } from "@/data/api/kili/consumption/types/consumption-metrics.type";
import { useTypedIntl } from "@/locales/typed-intl";
import { CalendarHeatmapXAxisAdapter } from "@/utils/chart/adapters/axis/calendar-heatmap-x-axis-adapter";
import { CalendarHeatmapSeriesAdapter } from "@/utils/chart/adapters/series/calendar-heatmap-series-adapter";
import { buildCalendarHeatmapOptions, ICalendarHeatmapSpecs } from "@/utils/chart/calendar-heatmap-options-utils";
import { getWhSeriesHighestPrefixSymbol } from "@/utils/chart/chart-series-utils";
import { EDay } from "@/utils/date";
import { capitalizeWords } from "@/utils/string-utils";
import { TLocalDate } from "@/utils/time";

import DashboardCard from "@/components/data/dashboard/dashboard-card.vue";
import BaseChart from "@/components/ui/chart/base-chart.vue";

interface IProps {
  isLoading?: boolean;
  serie?: TTemporalDataResponseSerie<EYearConsumption>;
  type: EDashboardType;
  title: string;
}

const props = defineProps<IProps>();
const intl = useTypedIntl();
const { t, k } = intl;

function weekdaysFormatter(day: EDay) {
  return t(k.global.weekDays[day]);
}

function xAxisTickMonthsFormatter(d: TLocalDate) {
  return capitalizeWords(intl.d(d, { month: "short", year: "2-digit" }));
}

const heatmapSpecs = computed<ICalendarHeatmapSpecs<EYearConsumption>>(() => {
  const maybePrefixSymbol = props.serie ? getWhSeriesHighestPrefixSymbol([props.serie]) : undefined;

  return {
    xAxisAdapter: CalendarHeatmapXAxisAdapter({
      opposite: true,
      xAxisMonthTickFormatter: xAxisTickMonthsFormatter,
    }),
    serieAdapter: CalendarHeatmapSeriesAdapter({
      prefixSymbol: maybePrefixSymbol,
      tooltipNullText: t(k.global.missingData),
    }),
    weekdaysFormatter,
    prefixSymbol: maybePrefixSymbol,
  };
});

const heatmapOpts = computed<Options | undefined>(() => {
  if (props.isLoading || !props.serie) return;

  return buildCalendarHeatmapOptions(heatmapSpecs.value, props.serie);
});
</script>

<template>
  <DashboardCard
    class="heatmap-consumption-chart"
    :title="props.title"
  >
    <template #title-icon>
      <div class="bg-gray-100 rounded-full !w-[20px] !h-[20px] flex justify-center items-center">
        <i class="pi pi-calendar text-text-secondary !text-sm" />
      </div>
    </template>

    <BaseChart
      :is-loading="props.isLoading"
      :options="heatmapOpts"
    >
      <template #noDataMessage>
        <slot name="noDataMessage" />
      </template>
    </BaseChart>
  </DashboardCard>
</template>
